/**
 * A deploy replaces the build's hashed files. A page loaded before it still
 * points at the old ones, so the next lazy page it opens fails with "Unable to
 * preload CSS" or a failed dynamic import, and the error boundary shows its
 * fallback. Vite announces such a failure as `vite:preloadError`: the page
 * then reloads once to fetch the new build. A second failure within
 * RELOAD_WINDOW_MS is a real outage, not an old page, so it goes on to the
 * error boundary instead of reloading in a loop.
 */

import { logger } from "@/lib/logger";

const STORAGE_KEY = "dc-reload-after-deploy";
export const RELOAD_WINDOW_MS = 10_000;

/** Whether to reload now, given the time of the last reload this tab made for it (0 for none). */
export function shouldReload(now: number, lastReload: number): boolean {
  return now - lastReload > RELOAD_WINDOW_MS;
}

/** Reload the page once when a file of an older build fails to load. */
export function reloadAfterDeploy(): void {
  window.addEventListener("vite:preloadError", (event) => {
    const now = Date.now();
    if (!shouldReload(now, readLastReload())) {
      logger.warn("[deploy] a file failed to load again right after a reload");
      return;
    }
    // Without a stored time the guard cannot stop a loop: leave it to the error boundary.
    if (!writeLastReload(now)) {
      logger.warn("[deploy] a file failed to load, and the reload guard cannot be stored");
      return;
    }
    event.preventDefault();
    logger.info("[deploy] a file of an older build is gone: reloading");
    window.location.reload();
  });
}

function readLastReload(): number {
  try {
    return Number(window.sessionStorage.getItem(STORAGE_KEY)) || 0;
  } catch {
    return 0;
  }
}

/** Store the time of this reload; false when the tab cannot store it. */
function writeLastReload(now: number): boolean {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(now));
    return window.sessionStorage.getItem(STORAGE_KEY) === String(now);
  } catch {
    return false;
  }
}
